import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";

import {
  DEFAULT_CHAT_MODEL,
  DEFAULT_MESSAGE_1,
  DEFAULT_MESSAGE_1_ROLE,
  DEFAULT_MESSAGE_2,
  DEFAULT_MESSAGE_2_ROLE,
} from "@/components/Playground/types";
import { useUserToken } from "@/context/auth-context";
import { useCreatePromptTemplate } from "@/queries";
import { Message } from "@/types/prompt-blueprint";
import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

const useCreateDefaultPrompt = (folderId?: number, isSnippet = false, openInNewTab = false) => {
  const userToken = useUserToken();
  const [isCreating, setIsCreating] = useState(false);

  const { workspaceId } = useParams();
  const query = useCreatePromptTemplate(userToken ?? "", folderId);
  const navigate = useNavigate();

  const create = async () => {
    if (!workspaceId) {
      return;
    }

    setIsCreating(true);

    try {
      await new Promise<void>((resolve, reject) => {
        const promptTemplate = isSnippet
          ? {
              type: "completion" as const,
              content: [DEFAULT_MESSAGE_2],
              input_variables: [],
              template_format: "jinja2" as const,
            }
          : {
              type: "chat" as const,
              messages: [
                {
                  template_format: "jinja2" as const,
                  role: DEFAULT_MESSAGE_1_ROLE,
                  content: [DEFAULT_MESSAGE_1],
                } as Message,
                {
                  template_format: "jinja2" as const,
                  role: DEFAULT_MESSAGE_2_ROLE,
                  content: [DEFAULT_MESSAGE_2],
                } as Message,
              ],
              tools: undefined,
              tool_choice: undefined,
              function_call: undefined,
              input_variables: [],
              functions: [],
            };

        query.mutate(
          {
            prompt_template: {
              workspace_id: Number(workspaceId),
              is_snippet: isSnippet || undefined,
            },
            prompt_version: {
              inference_client_name: null,
              metadata: isSnippet
                ? { markdown_enabled: true }
                : {
                    model: DEFAULT_CHAT_MODEL,
                    markdown_enabled: true,
                  },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              prompt_template: promptTemplate as any,
              provider_base_url_name: null,
              provider_id: undefined,
            },
          },
          {
            onSuccess: response => {
              if ("prompt_template" in response) {
                const promptId = response.prompt_template.id;
                displayToast("Prompt created successfully", ToastType.success);
                const url = `/workspace/${workspaceId}/prompt/${promptId}/version/1/playground`;

                if (openInNewTab) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
                resolve();
              } else {
                reject(new Error("Invalid response"));
              }
            },
            onError: error => {
              reject(error);
            },
          },
        );
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    create,
    isCreating: isCreating || query.isPending,
    isError: query.isError,
    error: query.error,
  };
};

export default useCreateDefaultPrompt;
